/**
 * Persistent log of offline-sync items that were dropped after non-retryable errors
 * (or too many transient failures). Survives page reload; visible in the outbox UI.
 */
const DB_NAME = "tpt-offline-sync-failure-log";
const DB_VERSION = 1;
const STORE = "failures";
const MAX_ENTRIES = 100;

export type OfflineSyncFailureKind = "crm" | "image";

export type OfflineSyncFailureRecord = {
  id: string;
  at: number;
  kind: OfflineSyncFailureKind;
  status: number;
  title: string;
  errorCode?: string;
  message?: string;
  dealId?: string;
  read: boolean;
};

const unreadListeners = new Set<(n: number) => void>();
const logCountListeners = new Set<(n: number) => void>();

function notifyUnreadListeners(): void {
  void getUnreadOfflineSyncFailureCount().then((n) => {
    unreadListeners.forEach((fn) => {
      try {
        fn(n);
      } catch {
        /* ignore */
      }
    });
  });
}

function notifyLogCountListeners(): void {
  void listOfflineSyncFailures().then((rows) => {
    const n = rows.length;
    logCountListeners.forEach((fn) => {
      try {
        fn(n);
      } catch {
        /* ignore */
      }
    });
  });
}

function notifyFailureLogListeners(): void {
  notifyUnreadListeners();
  notifyLogCountListeners();
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

export async function appendOfflineSyncFailure(
  entry: Omit<OfflineSyncFailureRecord, "id" | "at" | "read">,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const record: OfflineSyncFailureRecord = {
    ...entry,
    id: crypto.randomUUID(),
    at: Date.now(),
    read: false,
  };
  await idbRequest(store.add(record));

  const all = (await idbRequest(store.getAll())) as OfflineSyncFailureRecord[];
  if (all.length > MAX_ENTRIES) {
    const sorted = all.sort((a, b) => a.at - b.at);
    const toRemove = sorted.slice(0, all.length - MAX_ENTRIES);
    for (const row of toRemove) {
      await idbRequest(store.delete(row.id));
    }
  }

  db.close();
  console.warn("[OFFLINE-SYNC-FAILURE]", record);
  notifyFailureLogListeners();
}

export async function listOfflineSyncFailures(): Promise<OfflineSyncFailureRecord[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const rows = (await idbRequest(tx.objectStore(STORE).getAll())) as OfflineSyncFailureRecord[];
  db.close();
  return rows.sort((a, b) => b.at - a.at);
}

export async function getUnreadOfflineSyncFailureCount(): Promise<number> {
  const rows = await listOfflineSyncFailures();
  return rows.filter((r) => !r.read).length;
}

export async function markAllOfflineSyncFailuresRead(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const rows = (await idbRequest(store.getAll())) as OfflineSyncFailureRecord[];
  for (const row of rows) {
    if (!row.read) {
      await idbRequest(store.put({ ...row, read: true }));
    }
  }
  db.close();
  notifyFailureLogListeners();
}

export async function removeOfflineSyncFailure(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).delete(id));
  db.close();
  notifyFailureLogListeners();
}

export async function clearOfflineSyncFailureLog(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).clear());
  db.close();
  notifyFailureLogListeners();
}

export function subscribeOfflineSyncFailureUnreadCount(listener: (count: number) => void): () => void {
  unreadListeners.add(listener);
  void getUnreadOfflineSyncFailureCount().then(listener);
  return () => {
    unreadListeners.delete(listener);
  };
}

export function subscribeOfflineSyncFailureLogCount(listener: (count: number) => void): () => void {
  logCountListeners.add(listener);
  void listOfflineSyncFailures().then((rows) => listener(rows.length));
  return () => {
    logCountListeners.delete(listener);
  };
}

/** Parse JSON error body from a failed fetch response (best-effort). */
export async function readApiErrorBody(
  r: Response,
): Promise<{ errorCode?: string; message?: string }> {
  try {
    const body = (await r.json()) as { error?: string; message?: string };
    return {
      errorCode: body.error ? String(body.error) : undefined,
      message: body.message ? String(body.message) : undefined,
    };
  } catch {
    return {};
  }
}
